import { FEEDBACK } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useMemo } from 'react'

import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
} from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { IDENTITY } from 'constants/commonConstans'
import { t } from 'services/Translation'

import { BrandFieldShadow } from '../components'
import { BORDER_OPTIONS, DISPLAY_ICON_OPTIONS } from '../constants/constants'
import { genBrandField, genSchema } from '../genSchema'
import { useBrandColorOptions, useShowBorder, useThemeValueGetter } from './useFieldConfigs'

const useHiddenIcon = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const values = useFormData<{ common: { displayIcon?: boolean } }>(name, 2)
  const displayIcon = getter(values?.common?.displayIcon)

  return useMemo(() => ({ hidden: !displayIcon }), [displayIcon])
}

export const FEEDBACK_SCHEMA_FIELDS = genSchema(FEEDBACK.desktop, {
  correct: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('input.label.correctIcon'),
      layout: 'vertical',
      props: { preview: false, fileType: FileUsageTypeEnum.image },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  incorrect: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('input.label.incorrectIcon'),
      layout: 'vertical',
      props: { preview: false, fileType: FileUsageTypeEnum.image },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  common__var: {
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      props: { width: 64 },
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      props: { width: 64 },
    }),
    marginRight: genBrandField({
      as: FieldNumber,
      label: t('input.label.marginSide'),
      props: { width: 64 },
    }),
  },
  common: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    displayIcon: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('input.label.displayIcon'),
      props: {
        options: DISPLAY_ICON_OPTIONS,
      },
    }),
  },
  info: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('input.label.infoIcon'),
      layout: 'vertical',
      props: { preview: false, fileType: FileUsageTypeEnum.image },
      useConfigs: [useHiddenIcon],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  success: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('input.label.correctIcon'),
      layout: 'vertical',
      props: { preview: false, fileType: FileUsageTypeEnum.image },
      useConfigs: [useHiddenIcon],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  fail: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('input.label.incorrectIcon'),
      layout: 'vertical',
      props: { preview: false, fileType: FileUsageTypeEnum.image },
      useConfigs: [useHiddenIcon],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      label: t('elements.testFeedback.schema.boxShadow'),
      as: BrandFieldShadow,
      useConfigs: [useThemeValueGetter],
    }),
  },
  indents: {
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('input.label.answerMargin'),
      props: { width: 64 },
    }),
    marginBottom: genBrandField({
      as: FieldNumber,
      label: t('input.label.feedbackMargin'),
      props: { width: 64 },
    }),
  },
})
